/**
 * Orchestration hook for the Evidence Summary Suggestion banner (GRO-2767).
 *
 * Determines whether to show the banner after note generation based on:
 * - Feature flag enabled
 * - Note is completed
 * - Backend scoring says clinical signals warrant showing
 * - Tiered suppression: consecutive dismissals trigger a cooldown whose
 *   threshold and duration scale with the user's engagement history.
 *
 * On "Get summary": creates a source collection from recommendations,
 * activates it in source control, then opens evidence sidebar with the prompt.
 *
 * Dismissal and engagement are persisted via the seen_modals infrastructure
 * using action-based tracking (dismiss/engage).
 */

import { pendingChatMessageAtom, pendingEntryMethodAtom } from '@/atoms/chatLaunchAtoms';
import { activeSidebarChatIdAtom } from '@/atoms/chatSidebarAtoms';
import { evidenceSidebarOpenAtom } from '@/atoms/evidenceSidebarAtoms';
import { seenModalIdsAtom } from '@/atoms/modalQueueAtoms';
import { sourceControlToggleAtom } from '@/atoms/sourceControlAtoms';
import { EVIDENCE_SUMMARY_PROMPT } from '@/container/ScribeTabs/hooks/evidence-summary-prompt';
import { useIsEvidenceSummarySuggestionEnabled } from '@/features/flags';
import { useManualExposure } from '@/hooks/amplitude/use-manual-exposure';
import { useMutateMarkModalAsViewed } from '@/hooks/mutation/useMutateMarkModalAsViewed';
import { useQueryEvidenceSummarySuggestion } from '@/hooks/query/useQueryEvidenceSummarySuggestion';
import { useQuerySourceRecommendations } from '@/hooks/query/useQuerySourceRecommendations';
import { useQuerySpecialties } from '@/hooks/query/useQuerySpecialties';
import { useQueryUser } from '@/hooks/query/useQueryUser';
import { useEvidenceAccess } from '@/hooks/useEvidenceAccess';
import { track } from '@/lib/analytics/track';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { StreamingStatus } from '@/models/scribeRecordModel';
import { logger } from '@/utils/logger/logger';
import { useCreateSourceList, useSourceLists } from '@heidi/evidence/data';
import { ConsultNoteStatus, ModalId, ModalViewAction } from '@heidi/schema/ml-scribe';
import { countries } from '@heidi/utils/countries';
import { useSetAtom } from 'jotai';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
const MODAL_ID_EVIDENCE_SUMMARY_SUGGESTION = ModalId.EVIDENCE_SUMMARY_SUGGESTION;

/**
 * SessionStorage-backed tracker for sessions where the banner has already been
 * shown/dismissed/engaged. Survives page refreshes within the same tab.
 *
 * TODO: If this experiment is successful, migrate persistence from sessionStorage
 * to the SessionModel on the backend so the flag survives across devices and tab closures.
 */
const SEEN_SESSIONS_STORAGE_KEY = 'evidence-summary-banner-seen-sessions';

function getSeenSessions(): Set<string> {
  try {
    const raw = sessionStorage.getItem(SEEN_SESSIONS_STORAGE_KEY);
    if (!raw) return new Set();
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? new Set(parsed as string[]) : new Set();
  } catch {
    return new Set();
  }
}

function markSessionSeen(sessionId: string): void {
  try {
    const seen = getSeenSessions();
    seen.add(sessionId);
    sessionStorage.setItem(SEEN_SESSIONS_STORAGE_KEY, JSON.stringify([...seen]));
  } catch {
    // sessionStorage unavailable — fall through silently
  }
}

/**
 * Suppression tiers: the more a user has engaged, the more dismissals
 * we tolerate before suppressing, and the shorter the suppression window.
 */
const SUPPRESSION_TIERS = [
  { minEngagements: 10, dismissThreshold: 5, suppressionDays: 0 }, // session-only
  { minEngagements: 5, dismissThreshold: 4, suppressionDays: 7 },
  { minEngagements: 1, dismissThreshold: 3, suppressionDays: 14 },
  { minEngagements: 0, dismissThreshold: 2, suppressionDays: 30 },
] as const;

interface UseEvidenceSummarySuggestionProps {
  sessionId: string;
  consultNoteStatus: ConsultNoteStatus | undefined;
  streamingStatus: StreamingStatus | undefined;
  // When false, suppresses analytics/exposure side effects so visibility-only
  // parents don't double-fire events the banner component owns.
  trackImpressions?: boolean;
}

export function useEvidenceSummarySuggestion({
  sessionId,
  consultNoteStatus,
  streamingStatus,
  trackImpressions = true,
}: UseEvidenceSummarySuggestionProps) {
  const intl = useIntl();
  const isFlagEnabled = useIsEvidenceSummarySuggestionEnabled();
  const { isInSessionEvidenceShown } = useEvidenceAccess();
  const [dismissedThisSession, setDismissedThisSession] = useState(() =>
    getSeenSessions().has(sessionId)
  );

  // Re-evaluate when sessionId changes without a remount (e.g. parent re-render)
  useEffect(() => {
    setDismissedThisSession(getSeenSessions().has(sessionId));
  }, [sessionId]);

  const { mutate: markModalAsViewed } = useMutateMarkModalAsViewed();

  // Evidence sidebar atoms
  const setSeenModalIds = useSetAtom(seenModalIdsAtom);
  const setEvidenceSidebarOpen = useSetAtom(evidenceSidebarOpenAtom);
  const setActiveSidebarChatId = useSetAtom(activeSidebarChatIdAtom);
  const setPendingChatMessage = useSetAtom(pendingChatMessageAtom);
  const setPendingEntryMethod = useSetAtom(pendingEntryMethodAtom);
  const setSourceControlToggle = useSetAtom(sourceControlToggleAtom);

  // Source collection: reuse existing or create new
  const { mutateAsync: createSourceList } = useCreateSourceList();
  const { data: existingSourceLists } = useSourceLists();

  // Fetch source recommendations for avatar favicons, country, and collection creation
  const { data: recommendationsData } = useQuerySourceRecommendations(isFlagEnabled);
  const sources = useMemo(
    () => recommendationsData?.sources ?? [],
    [recommendationsData?.sources]
  );
  const matchedCountry = recommendationsData?.matched_country ?? '';
  const safeCountryCode = (
    countries.find((c) => c.name === matchedCountry)?.countryCode ?? matchedCountry
  ).trim();

  // Get user's specialty name for the collection name + activation flag.
  // Banner targets non-activated users (haven't crossed >=5 Evidence queries this week);
  // power users already engage with Evidence and don't need the prompt.
  const { data: userData } = useQueryUser((data) => {
    if (!data) return undefined;
    return {
      seen_modals: data.seen_modals,
      specialtyId: data.specialty_id,
      isEvidenceActivated: data.is_evidence_activated,
    };
  });
  // Guard against "loading" being treated as "not activated" — otherwise the
  // hook fires exposure + the backend query for users who are actually
  // activated before their profile hydrates.
  const isUserHydrated = userData !== undefined;
  const isEvidenceActivated = userData?.isEvidenceActivated ?? false;

  const { data: specialtiesData } = useQuerySpecialties();
  const specialtyName =
    specialtiesData?.specialties.find((s) => s.id === userData?.specialtyId)
      ?.localizedName ?? '';

  // Check note completion: consult note is completed and streaming has stopped
  const isNoteCompleted =
    consultNoteStatus === ConsultNoteStatus.COMPLETED &&
    (streamingStatus === StreamingStatus.NOT_STARTED || streamingStatus === undefined);

  const modalRecord = userData?.seen_modals?.[MODAL_ID_EVIDENCE_SUMMARY_SUGGESTION];
  const consecutiveDismissals = modalRecord?.consecutive_dismiss_count ?? 0;
  const engageCount = modalRecord?.engage_count ?? 0;
  const lastSeenAt = modalRecord?.last_seen_at;

  // Find the matching suppression tier based on engagement history
  const tier = SUPPRESSION_TIERS.find((t) => engageCount >= t.minEngagements)!;

  const isSuppressed = useMemo(() => {
    if (consecutiveDismissals < tier.dismissThreshold) return false;
    // Session-only suppression (tier with 0 days) is handled by dismissedThisSession
    if (tier.suppressionDays === 0) return false;
    if (!lastSeenAt) return false;
    const suppressedUntil = new Date(lastSeenAt);
    suppressedUntil.setDate(suppressedUntil.getDate() + tier.suppressionDays);
    return new Date() < suppressedUntil;
  }, [consecutiveDismissals, tier, lastSeenAt]);

  // Manual exposure for Amplitude experiment analysis (GRO-2767)
  // Eligibility excludes the flag gate so control-arm users are also tracked.
  const isExposureEligible =
    isUserHydrated &&
    !!isInSessionEvidenceShown &&
    !isEvidenceActivated &&
    isNoteCompleted &&
    !isSuppressed &&
    !dismissedThisSession;
  useManualExposure({
    flagKey: 'release-evidence-enable-singular-summary-suggestion',
    isEnabled: isFlagEnabled,
    isVisible: trackImpressions && isExposureEligible,
  });

  // Query the backend scoring endpoint
  const shouldQueryBackend =
    isUserHydrated &&
    isFlagEnabled &&
    isInSessionEvidenceShown &&
    !isEvidenceActivated &&
    isNoteCompleted &&
    !isSuppressed &&
    !dismissedThisSession;

  const { data: suggestionData } = useQueryEvidenceSummarySuggestion({
    sessionId,
    enabled: shouldQueryBackend,
  });

  const shouldShow = shouldQueryBackend && suggestionData?.should_show === true;

  const score = suggestionData?.score ?? 0;
  const signals = useMemo(() => suggestionData?.signals ?? [], [suggestionData?.signals]);

  // Track banner shown event (fire once per session). Skipped for
  // visibility-only callers so the banner component remains the single owner.
  const hasTrackedShown = useRef(false);
  useEffect(() => {
    if (!trackImpressions) return;
    if (shouldShow && !hasTrackedShown.current) {
      hasTrackedShown.current = true;
      track({
        event_name: TrackingEventName.EvidenceSummarySuggestionShown,
        props: { session_id: sessionId, score, signals },
        options: { amplitude: false },
      });
    }
  }, [trackImpressions, shouldShow, sessionId, score, signals]);

  const onDismiss = useCallback(() => {
    setDismissedThisSession(true);
    markSessionSeen(sessionId);
    // Optimistic update so the popover Apply banner can gate on this immediately
    setSeenModalIds(
      (prev) => new Set([...(prev ?? []), MODAL_ID_EVIDENCE_SUMMARY_SUGGESTION])
    );
    markModalAsViewed({
      modal_id: MODAL_ID_EVIDENCE_SUMMARY_SUGGESTION,
      increment_count: true,
      action: ModalViewAction.dismiss,
    });
    track({
      event_name: TrackingEventName.EvidenceSummarySuggestionDismissed,
      props: {
        session_id: sessionId,
        dismiss_count: consecutiveDismissals + 1,
      },
      options: { amplitude: false },
    });
  }, [setSeenModalIds, markModalAsViewed, sessionId, consecutiveDismissals]);

  /** Activate a collection in source control as a single atomic update. */
  const activateCollection = useCallback(
    (collectionId: string) => {
      setSourceControlToggle((prev) => ({
        ...prev,
        webCollections: true,
        webCollectionsMode: 'collections' as const,
        selectedWebCollectionIds: [collectionId],
      }));
    },
    [setSourceControlToggle]
  );

  const onGenerate = useCallback(async () => {
    setDismissedThisSession(true);
    markSessionSeen(sessionId);
    markModalAsViewed({
      modal_id: MODAL_ID_EVIDENCE_SUMMARY_SUGGESTION,
      increment_count: true,
      action: ModalViewAction.engage,
    });

    // Create (or reuse) the recommended source collection and activate it
    if (sources.length > 0) {
      try {
        const domains = sources.map((s) => s.domain);
        const name =
          specialtyName && safeCountryCode
            ? intl.formatMessage(
                { defaultMessage: '{specialty} - {countryCode}', id: 'qu5ct86XXl' },
                { specialty: specialtyName, countryCode: safeCountryCode }
              )
            : specialtyName || safeCountryCode;

        if (name) {
          // Reuse existing collection with same name, or create a new one
          const existing = existingSourceLists?.find((l) => l.name === name);
          if (existing) {
            activateCollection(existing.id);
          } else {
            const newList = await createSourceList({ name, domains });
            activateCollection(newList.id);
          }
        }
      } catch (error) {
        logger.error(
          '[Evidence Summary Suggestion] Failed to create source collection',
          error
        );
      }
    }

    // Open evidence in the in-session side panel with the fixed prompt
    setActiveSidebarChatId(null);
    setPendingChatMessage(EVIDENCE_SUMMARY_PROMPT);
    setPendingEntryMethod('suggestion_chip');
    setEvidenceSidebarOpen(true);

    track({
      event_name: TrackingEventName.EvidenceSummarySuggestionGenerated,
      props: { session_id: sessionId, suggested_query: EVIDENCE_SUMMARY_PROMPT },
      options: { amplitude: false },
    });
  }, [
    markModalAsViewed,
    sessionId,
    sources,
    specialtyName,
    safeCountryCode,
    intl,
    existingSourceLists,
    createSourceList,
    activateCollection,
    setActiveSidebarChatId,
    setPendingChatMessage,
    setPendingEntryMethod,
    setEvidenceSidebarOpen,
  ]);

  return {
    shouldShow,
    sources,
    matchedCountry,
    score,
    signals,
    onDismiss,
    onGenerate,
  };
}
